<template>
  <div class="learning-plan-container">
    <div class="plan-header">
      <h3>学习计划</h3>
      <div v-if="currentPlan" class="plan-info">
        <span class="plan-name">{{ currentPlan.planName }}</span>
        <span class="plan-meta">{{ currentPlan.academicYear }} {{ currentPlan.semester }}</span>
      </div>
    </div>

    <!-- 教学活动时间轴 -->
    <div v-if="currentPlan && activities.length > 0" class="timeline-wrapper">
      <div class="timeline-container">
        <el-timeline>
        <el-timeline-item
          v-for="activity in activities"
          :key="activity.id"
          :timestamp="activity.activityDate"
          placement="top"
          :type="getActivityType(activity.activityType)"
          size="large"
          :icon="getActivityIcon(activity.activityType)">
          <el-card
            :ref="`activity-${activity.id}`"
            class="activity-card"
            :class="{ 'current-activity': isCurrentActivity(activity) }"
            @click.native="viewActivityDetail(activity)">
            <div class="activity-header">
              <span class="activity-type">{{ activity.activityType }}</span>
              <span v-if="activity.title" class="activity-title">{{ activity.title }}</span>
              <el-tag v-if="getActivityStatus(activity.id) === 1" type="success" size="small">
                <i class="el-icon-check"></i> 已完成
              </el-tag>
              <el-tag v-else type="info" size="small">
                <i class="el-icon-time"></i> 未完成
              </el-tag>
            </div>
            <div v-if="activity.location" class="activity-location">
              <i class="el-icon-location-outline"></i>
              {{ activity.location }}
            </div>
            <div v-if="activity.duration" class="activity-duration">
              <i class="el-icon-time"></i>
              时长：{{ activity.duration }}分钟
            </div>
            <div v-if="activity.description" class="activity-description">
              {{ activity.description }}
            </div>
            <div class="activity-footer">
              <el-button type="text" icon="el-icon-view">查看详情</el-button>
            </div>
          </el-card>
        </el-timeline-item>
      </el-timeline>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-else-if="loading" class="empty-state">
      <i class="el-icon-loading"></i>
      <p>正在加载学习计划...</p>
    </div>
    <div v-else-if="!currentPlan" class="empty-state">
      <i class="el-icon-info"></i>
      <p>该课程暂无教学计划</p>
    </div>
    <div v-else-if="currentPlan && activities.length === 0" class="empty-state">
      <i class="el-icon-document"></i>
      <p>该教学计划暂无教学活动</p>
    </div>

    <!-- 教学活动详情弹窗 -->
    <el-dialog
      title="教学活动详情"
      :visible.sync="activityDetailVisible"
      width="800px"
      modal
      :close-on-click-modal="true">
      <div v-if="currentActivity" class="activity-detail">
        <!-- 活动基本信息 -->
        <div class="detail-section">
          <h4 class="section-title">活动信息</h4>
          <el-table :data="[{activity: currentActivity}]" border :show-header="false">
            <el-table-column prop="type" label="活动类型" width="120">
              <template slot-scope="scope">{{ scope.row.activity.activityType }}</template>
            </el-table-column>
            <el-table-column prop="date" label="活动日期" width="150">
              <template slot-scope="scope">{{ scope.row.activity.activityDate }}</template>
            </el-table-column>
            <el-table-column prop="location" label="活动地点">
              <template slot-scope="scope">{{ scope.row.activity.location || '未设置' }}</template>
            </el-table-column>
            <el-table-column prop="duration" label="活动时长" width="120">
              <template slot-scope="scope">{{ scope.row.activity.duration || '未设置' }}分钟</template>
            </el-table-column>
          </el-table>
          <div v-if="currentActivity.description" class="activity-description-detail">
            <strong>活动描述：</strong>{{ currentActivity.description }}
          </div>
        </div>

        <!-- 学习视频 -->
        <div class="detail-section" v-if="videoResources.length > 0">
          <h4 class="section-title">学习视频</h4>
          <div class="video-list">
            <div
              v-for="resource in videoResources"
              :key="resource.id"
              class="video-item"
              @click.stop="viewResource(resource)">
              <i class="el-icon-video-camera"></i>
              <span class="video-name">{{ resource.name }}</span>
              <el-button type="text" icon="el-icon-video-play" @click.stop="viewResource(resource)">播放</el-button>
            </div>
          </div>
        </div>

        <!-- 教学资源 -->
        <div class="detail-section">
          <h4 class="section-title">教学资源</h4>
          <div v-if="otherResources.length > 0" class="resource-list">
            <div
              v-for="resource in otherResources"
              :key="resource.id"
              class="resource-item"
              @click.stop="viewResource(resource)">
              <i class="el-icon-document"></i>
              <span class="resource-name">{{ resource.name }}</span>
              <el-button type="text" icon="el-icon-download" @click.stop="">下载</el-button>
            </div>
          </div>
          <div v-else class="empty-resource">
            <i class="el-icon-document"></i>
            <span>暂无教学资源</span>
          </div>
        </div>

        <!-- 关联作业 -->
        <div class="detail-section">
          <h4 class="section-title">关联作业</h4>
          <div v-if="currentActivity.homeworks && currentActivity.homeworks.length > 0" class="homework-list">
            <div
              v-for="homework in currentActivity.homeworks"
              :key="homework.id"
              class="homework-item"
              @click.stop="handleHomeworkClick(homework)">
              <i class="el-icon-folder"></i>
              <span class="homework-name">{{ homework.name }}</span>
              <el-button type="text" icon="el-icon-arrow-right" @click.stop="handleHomeworkClick(homework)">去完成</el-button>
            </div>
          </div>
          <div v-else class="empty-homework">
            <i class="el-icon-folder"></i>
            <span>暂无关联作业</span>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click.native.stop="closeActivityDetail">关 闭</el-button>
      </span>
    </el-dialog>

  </div>
</template>

<script>
import { getTeachingPlans, getActivitiesByPlan, getStudentActivityStatus } from '@/api/teachingPlan'
import { downloadCourseResource } from '@/api/courseResource'

export default {
  name: 'LearningPlan',
  props: {
    courseId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      currentPlan: null,
      activities: [],
      loading: false,
      activityDetailVisible: false,
      currentActivity: null,
      activityStatusMap: {}, // 活动完成状态映射 {activityId: status}
      currentActivityId: null // 当前时间最近的活动ID
    }
  },
  created() {
    if (this.courseId) {
      this.loadTeachingPlan()
    }
  },
  watch: {
    courseId: {
      handler(newVal) {
        if (newVal) {
          this.loadTeachingPlan()
        }
      },
      immediate: false
    }
  },
  computed: {
    // 视频资源（mp4, avi, mov, wmv, flv, webm, mkv等）
    videoResources() {
      if (!this.currentActivity || !this.currentActivity.resources) {
        return []
      }
      const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv']
      return this.currentActivity.resources.filter(resource => {
        // 检查文件名或URL是否包含视频扩展名
        const name = resource.name || ''
        const url = resource.url || ''
        const fileName = name.toLowerCase() || url.toLowerCase()
        return videoExtensions.some(ext => fileName.endsWith('.' + ext))
      })
    },
    // 其他非视频资源
    otherResources() {
      if (!this.currentActivity || !this.currentActivity.resources) {
        return []
      }
      const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv']
      return this.currentActivity.resources.filter(resource => {
        const name = resource.name || ''
        const url = resource.url || ''
        const fileName = name.toLowerCase() || url.toLowerCase()
        return !videoExtensions.some(ext => fileName.endsWith('.' + ext))
      })
    },
    // 获取最近的活动ID
    nearestActivityId() {
      if (!this.activities || this.activities.length === 0) {
        return null
      }

      const now = new Date()
      now.setHours(0, 0, 0, 0) // 只比较日期，不比较时间

      // 找到第一个大于等于今天的活动
      for (const activity of this.activities) {
        const activityDate = new Date(activity.activityDate)
        activityDate.setHours(0, 0, 0, 0)

        if (activityDate >= now) {
          return activity.id
        }
      }

      // 如果没有找到大于今天的活动，返回最后一个活动
      return this.activities[this.activities.length - 1].id
    }
  },
  methods: {
    // 加载教学计划
    async loadTeachingPlan() {
      if (!this.courseId) {
        console.warn('courseId未定义，无法加载教学计划')
        return
      }

      console.log('开始加载教学计划，courseId:', this.courseId)
      this.loading = true
      try {
        const response = await getTeachingPlans(this.courseId)
        console.log('教学计划API响应:', response)
        if (response.code === 1) {
          const plans = response.data || []
          console.log('获取到的教学计划列表:', plans)
          if (plans.length > 0) {
            // 如果有多个计划，选择第一个
            this.currentPlan = plans[0]
            console.log('选择的教学计划:', this.currentPlan)
            await this.loadActivities()
          } else {
            console.warn('该课程没有教学计划')
            this.currentPlan = null
          }
        } else {
          console.error('API返回错误:', response.msg)
        }
      } catch (error) {
        console.error('加载教学计划失败:', error)
        this.$message.error('加载教学计划失败')
      } finally {
        this.loading = false
      }
    },

    // 加载活动列表
    async loadActivities() {
      if (!this.currentPlan) {
        console.warn('没有选择教学计划，无法加载活动列表')
        return
      }

      console.log('开始加载活动列表，planId:', this.currentPlan.id)
      try {
        const response = await getActivitiesByPlan(this.currentPlan.id)
        console.log('活动列表API响应:', response)
        if (response.code === 1) {
          this.activities = response.data || []
          console.log('获取到的活动列表:', this.activities)
          // 打印每个活动的ID类型
          this.activities.forEach(activity => {
            console.log('活动ID:', activity.id, '类型:', typeof activity.id)
          })
          // 加载完成后，获取学生活动完成状态
          await this.loadActivityStatus()

          // 滚动到最近的活动
          this.$nextTick(() => {
            this.scrollToCurrentActivity()
          })
        } else {
          console.error('API返回错误:', response.msg)
        }
      } catch (error) {
        console.error('加载活动列表失败:', error)
        this.$message.error('加载活动列表失败')
      }
    },

    // 加载学生活动完成状态
    async loadActivityStatus() {
      try {
        // 获取当前学生ID
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
        const studentId = userInfo.studentId || userInfo.data?.studentId || userInfo.id

        if (!studentId) {
          console.warn('无法获取学生ID，跳过加载活动完成状态')
          return
        }

        console.log('加载学生活动完成状态，studentId:', studentId, 'courseId:', this.courseId)
        const response = await getStudentActivityStatus(studentId, this.courseId)
        console.log('活动完成状态API响应:', response)

        if (response.code === 1) {
          // 将后端返回的字符串键转换为数字键
          const rawData = response.data || {}
          this.activityStatusMap = {}
          for (const key in rawData) {
            this.activityStatusMap[parseInt(key)] = rawData[key]
          }
          console.log('处理后的活动完成状态映射:', this.activityStatusMap)
        } else {
          console.error('获取活动完成状态失败:', response.msg)
        }
      } catch (error) {
        console.error('加载活动完成状态失败:', error)
      }
    },

    // 查看活动详情
    viewActivityDetail(activity) {
      console.log('点击查看活动详情:', activity)
      this.currentActivity = activity
      this.activityDetailVisible = true
      console.log('弹窗状态:', this.activityDetailVisible)
      console.log('当前活动数据:', this.currentActivity)
    },

    // 查看资源
    async viewResource(resource) {
      console.log('=== 查看资源 ===')
      console.log('资源对象:', resource)
      console.log('资源ID:', resource?.id)
      console.log('资源URL:', resource?.url)
      console.log('资源名称:', resource?.name)

      try {
        // 如果资源有URL，直接打开
        if (resource && resource.url) {
          console.log('使用资源URL直接打开:', resource.url)
          const url = resource.url
          // 确保URL以http开头
          if (url.startsWith('http://') || url.startsWith('https://')) {
            window.open(url, '_blank')
          } else {
            console.warn('资源URL格式不正确:', url)
            this.$message.warning('资源URL格式不正确')
          }
          return
        }

        // 如果资源有id，通过API下载
        if (resource && resource.id) {
          console.log('资源没有URL，通过API下载，资源ID:', resource.id)
          const response = await downloadCourseResource(resource.id)

          // 获取文件扩展名
          const fileName = resource.name || 'resource'
          const fileExtension = fileName.split('.').pop().toLowerCase()
          let mimeType = 'application/octet-stream'

          // 根据文件扩展名设置MIME类型
          switch (fileExtension) {
            case 'pdf':
              mimeType = 'application/pdf'
              break
            case 'doc':
              mimeType = 'application/msword'
              break
            case 'docx':
              mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
              break
            case 'xls':
              mimeType = 'application/vnd.ms-excel'
              break
            case 'xlsx':
              mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              break
            case 'ppt':
              mimeType = 'application/vnd.ms-powerpoint'
              break
            case 'pptx':
              mimeType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
              break
            case 'zip':
              mimeType = 'application/zip'
              break
            case 'rar':
              mimeType = 'application/x-rar-compressed'
              break
            case 'txt':
              mimeType = 'text/plain'
              break
            case 'jpg':
            case 'jpeg':
              mimeType = 'image/jpeg'
              break
            case 'png':
              mimeType = 'image/png'
              break
            case 'gif':
              mimeType = 'image/gif'
              break
          }

          // 创建Blob对象
          const blob = new Blob([response.data], { type: mimeType })

          // 创建下载链接
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = fileName
          link.style.display = 'none'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)

          this.$message.success('下载成功')
        } else {
          console.warn('资源数据无效')
          this.$message.warning('资源数据无效')
        }
      } catch (error) {
        console.error('下载失败:', error)
        this.$message.error('下载失败: ' + (error.message || '未知错误'))
      }
    },

    // 获取活动类型颜色
    getActivityType(type) {
      const typeMap = {
        '理论课': 'primary',
        '实践课': 'success',
        '实验课': 'warning',
        '讨论课': 'info',
        '复习课': 'danger',
        '考试': 'danger',
        '作业': 'info',
        '其他': ''
      }
      return typeMap[type] || ''
    },

    // 获取活动类型图标
    getActivityIcon(type) {
      const iconMap = {
        '理论课': 'el-icon-reading',
        '实践课': 'el-icon-edit-outline',
        '实验课': 'el-icon-location-outline',
        '讨论课': 'el-icon-chat-line-round',
        '复习课': 'el-icon-refresh',
        '考试': 'el-icon-document',
        '作业': 'el-icon-folder',
        '其他': 'el-icon-more'
      }
      return iconMap[type] || 'el-icon-more'
    },

    // 获取活动完成状态
    getActivityStatus(activityId) {
      // 确保ID类型匹配
      const id = parseInt(activityId)
      const status = this.activityStatusMap[id]
      console.log('原始活动ID:', activityId, '转换后ID:', id, '完成状态:', status, '活动状态映射:', this.activityStatusMap)
      return status !== undefined ? status : -1 // -1表示未知状态
    },

    // 判断是否是当前时间最近的活动
    isCurrentActivity(activity) {
      return activity.id === this.nearestActivityId
    },

    // 滚动到最近的活动
    scrollToCurrentActivity() {
      if (!this.nearestActivityId) {
        console.log('没有找到最近的活动')
        return
      }

      console.log('滚动到最近的活动，ID:', this.nearestActivityId)

      // 使用setTimeout确保DOM已完全渲染
      setTimeout(() => {
        const refName = `activity-${this.nearestActivityId}`
        const refs = this.$refs[refName]

        if (refs && refs.length > 0) {
          const element = refs[0].$el || refs[0]
          if (element) {
            // 平滑滚动到该元素
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            })
            console.log('已滚动到活动ID:', this.nearestActivityId)
          }
        } else {
          console.warn('未找到活动元素，refName:', refName)
        }
      }, 300)
    },

    // 关闭活动详情弹窗
    closeActivityDetail() {
      console.log('点击关闭按钮')
      this.activityDetailVisible = false
      this.currentActivity = null
      console.log('弹窗已关闭')
    },

    // 处理作业点击
    handleHomeworkClick(homework) {
      console.log('点击关联作业:', homework)
      // 显示提示信息
      this.$message({
        message: '请到作业汇总中完成相关任务',
        type: 'info',
        duration: 2000
      })
      // 通知父组件切换到作业汇总页面
      this.$emit('go-to-homework')
      // 关闭活动详情弹窗
      this.closeActivityDetail()
    }

  }
}
</script>

<style scoped lang="scss">
.learning-plan-container {
  padding: 20px;

  .plan-header {
    margin-bottom: 20px;

    h3 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
    }

    .plan-info {
      margin-top: 10px;
      color: #606266;
      font-size: 14px;

      .plan-name {
        font-weight: 600;
        color: #409eff;
        margin-right: 10px;
      }

      .plan-meta {
        color: #909399;
      }
    }
  }

  .timeline-wrapper {
    // 设置固定高度和滚动
    height: calc(100vh - 200px);
    overflow-y: auto;
    padding-right: 10px;

    // 自定义滚动条样式
    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 4px;

      &:hover {
        background: #a8a8a8;
      }
    }
  }

  .timeline-container {
    .activity-card {
      cursor: pointer;
      transition: all 0.3s;

      // 当前活动的特殊样式
      &.current-activity {
        border: 2px solid #409eff;
        box-shadow: 0 4px 16px rgba(64, 158, 255, 0.3);
        background-color: #f0f9ff;
      }

      &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        transform: translateY(-2px);
      }

      .activity-header {
        display: flex;
        align-items: center;
        margin-bottom: 10px;
        position: relative;

        .activity-type {
          font-weight: 600;
          color: #409eff;
          margin-right: 10px;
        }

        .activity-title {
          font-size: 16px;
          color: #303133;
          flex: 1;
        }

        .el-tag {
          position: absolute;
          right: 0;
          top: 0;
        }
      }

      .activity-location,
      .activity-duration {
        margin-bottom: 8px;
        color: #606266;
        font-size: 14px;

        i {
          margin-right: 5px;
        }
      }

      .activity-description {
        color: #909399;
        font-size: 14px;
        margin-bottom: 10px;
        line-height: 1.6;
      }

      .activity-footer {
        text-align: right;
      }
    }
  }

  .empty-state {
    text-align: center;
    padding: 60px 20px;
    color: #909399;

    i {
      font-size: 60px;
      margin-bottom: 20px;
      display: block;
    }

    p {
      font-size: 16px;
    }
  }

  .activity-detail {
    .detail-section {
      margin-bottom: 30px;

      .section-title {
        font-size: 16px;
        font-weight: 600;
        margin-bottom: 15px;
        color: #303133;
      }

      .resource-list,
      .homework-list,
      .video-list {
        .resource-item {
          display: flex;
          align-items: center;
          padding: 12px;
          margin-bottom: 10px;
          background-color: #f5f7fa;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.3s;

          &:hover {
            background-color: #e4e7ed;
          }
        }

        .video-item {
          display: flex;
          align-items: center;
          padding: 12px;
          margin-bottom: 10px;
          background-color: #e3f2fd;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.3s;

          &:hover {
            background-color: #bbdefb;
            box-shadow: 0 2px 8px rgba(33, 150, 243, 0.2);
          }

          i {
            font-size: 18px;
            color: #2196f3;
            margin-right: 10px;
          }

          .video-name {
            flex: 1;
            font-size: 14px;
            color: #303133;
            font-weight: 500;
          }
        }

        .homework-item {
          display: flex;
          align-items: center;
          padding: 12px;
          margin-bottom: 10px;
          background-color: #f5f7fa;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.3s;

          &:hover {
            background-color: #e4e7ed;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }

          i {
            font-size: 18px;
            color: #409eff;
            margin-right: 10px;
          }

          .homework-name {
            flex: 1;
            font-size: 14px;
            color: #303133;
          }
        }

        .resource-item {
          i {
            font-size: 18px;
            color: #409eff;
            margin-right: 10px;
          }

          .resource-name {
            flex: 1;
            font-size: 14px;
            color: #303133;
          }
        }
      }

      .empty-resource,
      .empty-homework {
        text-align: center;
        padding: 30px;
        color: #909399;

        i {
          font-size: 40px;
          margin-bottom: 10px;
          display: block;
        }
      }
    }
  }

  .activity-description-detail {
    margin-top: 15px;
    padding: 15px;
    background-color: #f5f7fa;
    border-radius: 4px;
    line-height: 1.6;
    color: #606266;

    strong {
      color: #303133;
    }
  }
}
</style>
